import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MdCall } from "react-icons/md";
import axios from "axios";
import { io } from "socket.io-client";

const DoNotDisturb = ({user}) => {
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dndData, setDndData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1); // Pagination state
  const recordsPerPage = 5; // 5 records per page

  // Fetch DnD list from API
  const fetchDndList = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${process.env.REACT_APP_SERVER_API}/api/dnd-list?userId=${user.id}&role=${user.role}`);
      setDndData(response.data);
    } catch (err) {
      setError("Failed to fetch DnD list. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch initial data and set up WebSocket listener
  useEffect(() => {
    fetchDndList();

    const socket = io(process.env.REACT_APP_SERVER_API);
    socket.on("dndUpdate", () => {
      fetchDndList(); // Refresh when a new DND entry is added
    });

    return () => socket.disconnect();
  }, [user]);

  // Filter data based on search
  const filteredData = dndData.filter((row) =>
    row.from.toLowerCase().includes(search.toLowerCase())
  );

  // Pagination logic
  const totalRecords = filteredData.length;
  const totalPages = Math.ceil(totalRecords / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const exportToCSV = () => {
    const csvContent = [
      ["ID", "From", "To", "Start Time", "End Time", "Sentiment Status"],
      ...filteredData.map((row, index) => [
        index + 1, // Continuous ID across all records
        row.from,
        row.to,
        row.startTime,
        row.endTime,
        row.agentStatus,
      ]),
    ]
      .map((e) => e.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "dnd_list.csv";
    link.click();
  };

  const exportToXLSX = () => {
    console.log("Export to XLSX functionality requires 'xlsx' library");
  };

  // Pagination handlers
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="flex justify-between items-center mb-6"
      >
        <h1 className="text-3xl font-bold text-gray-900">Do Not Disturb</h1>
        <div className="space-x-4">
          <button
            className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 focus:outline-none"
            onClick={exportToCSV}
          >
            Export to CSV
          </button>
          <button
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 focus:outline-none"
            onClick={exportToXLSX}
          >
            Export to XLSX
          </button>
        </div>
      </motion.div>

      {/* Search Bar */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="mb-6"
      >
        <input
          type="text"
          placeholder="Search by From Number"
          className="w-full md:w-1/4 px-4 py-3 border border-gray-300 rounded-xl shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300 ease-in-out transform focus:scale-105"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </motion.div>

      {/* Loading and Error States */}
      {loading && (
        <motion.div
          className="flex justify-center items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="animate-spin w-8 h-8 border-4 border-t-4 border-blue-500 rounded-full"></div>
        </motion.div>
      )}
      {error && (
        <motion.p
          className="text-red-500"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {error}
        </motion.p>
      )}

      {/* Dynamic Table */}
      {!loading && !error && (
        <>
          <motion.div
            className="overflow-x-auto bg-white shadow-lg rounded-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-md">
              <thead className="sticky top-0 bg-gradient-to-r from-blue-500 to-blue-700 text-white">
                <tr>
                  <th className="px-6 py-3 text-left">ID</th>
                  <th className="px-6 py-3 text-left">From</th>
                  <th className="px-6 py-3 text-left">To</th>
                  <th className="px-6 py-3 text-left">Start Time</th>
                  <th className="px-6 py-3 text-left">End Time</th>
                  <th className="px-6 py-3 text-left">Sentiment Status</th>
                  <th className="px-6 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((row, index) => (
                    <tr key={index} className="border-t">
                      <td className="px-6 py-4">{startIndex + index + 1}</td> {/* Continuous ID */}
                      <td className="px-6 py-4">{row.from}</td>
                      <td className="px-6 py-4">{row.to}</td>
                      <td className="px-6 py-4">{row.startTime}</td>
                      <td className="px-6 py-4">{row.endTime}</td>
                      <td className="px-6 py-4">{row.agentStatus}</td>
                      <td className="px-6 py-4">
                        <button className="text-blue-500 hover:text-blue-700">
                          <MdCall size={24} />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="px-6 py-4 text-center">
                      No DnD records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </motion.div>

          {/* Pagination Controls */}
          {totalRecords > 0 && (
            <div className="flex justify-center mt-4 space-x-2">
              <button
                className={`px-4 py-2 rounded-lg ${currentPage === 1 ? "bg-gray-300" : "bg-blue-500 text-white hover:bg-blue-600"}`}
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  className={`px-4 py-2 rounded-lg ${currentPage === page ? "bg-blue-700 text-white" : "bg-gray-200 hover:bg-gray-300"}`}
                  onClick={() => goToPage(page)}
                >
                  {page}
                </button>
              ))}
              <button
                className={`px-4 py-2 rounded-lg ${currentPage === totalPages ? "bg-gray-300" : "bg-blue-500 text-white hover:bg-blue-600"}`}
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DoNotDisturb;